import { ActivatedRouteSnapshot, Router, RouterModule, Routes } from '@angular/router';
import { authGuard } from './guards/auth-guard/auth.guard';
import { NgModule, inject } from '@angular/core';
import { PasswordResetComponent } from './modules/password-reset/password-reset.component';
import { EcommerceGuard } from './guards/ecommerce-guard/ecommerce.guard';
import { MIRROR_MODULES } from './modules/crm-mirror/crm-mirror.config';

export const routes: Routes = [
    {
        path: "custm-login",
        loadChildren: () => import('./modules/login/login.module').then(m => m.LoginModule)
    },
    // Driver onboarding is now the Workflow > Forms driver-registration form
    // (it replaced the onboarding app ported into modules/onboarding).
    // /registration starts a new driver; /registration/<guid> (a driver's or
    // CRM lead's, e.g. the Contractor CRM record's "continue registration")
    // is the form on their link (?r=), which moves their onboarding progress
    // on per step and completes this same record.
    { path: "registration", pathMatch: "full", redirectTo: "forms/driver-registration" },
    // (A guard, not redirectTo - Angular 17 can't put a path param into the query.)
    {
        path: "registration/:guid",
        canActivate: [(route: ActivatedRouteSnapshot) => inject(Router).createUrlTree(['/forms/driver-registration'], { queryParams: { r: route.paramMap.get('guid') } })],
        children: []
    },
    // The company Account Application is now a Workflow > Forms form.
    { path: "company-registration", redirectTo: "forms/company-registration" },
    {
        path: "forms/supplier-registration",
        loadComponent: () => import('./modules/supplier-crm/registration/supplier-registration.component').then(c => c.SupplierRegistrationComponent)
    },
    {
        path: "forms/supplier-onboarding",
        loadComponent: () => import('./modules/supplier-crm/onboarding/supplier-onboarding.component').then(c => c.SupplierOnboardingComponent)
    },
    {
        // Public Workflow > Forms page - no authGuard, same as registration
        // above. Calls the API's public-forms routes (see CrmFormService).
        path: "forms/:slug",
        loadComponent: () => import('./modules/public-form/public-form.component').then(c => c.PublicFormComponent)
    },
    {
        // Workflow > Forms studio: the form editor full-screen in its own tab, with a live
        // preview beside it (FormEditorComponent's studio layout). /form-studio/new starts a form.
        path: "form-studio/:id",
        loadComponent: () => import('./modules/workflow/components/form-editor/form-editor.component').then(c => c.FormEditorComponent),
        data: { studio: true },
        canActivate: [authGuard]
    },
    {
        // Short link for the "Apply to Drive" Workflow form (see the API's
        // crm:install-apply-to-drive-form) - e.g. the website's button.
        path: "apply-to-drive",
        redirectTo: "forms/apply-to-drive"
    },
    {
        path: "financial-dashboard",
        loadChildren: () => import('./modules/financial-dashboard/financial-dashboard.module').then(m => m.FinancialDashboardModule),
        canActivate: [authGuard]
    },
    { 
        path: 'dedi-flexi-hub/:shiftTypeId', 
        loadChildren: () => import('./modules/dedi-flexi-hub/dedi-flexi-hub.module').then(m => m.DediFlexiHubModule),
        canActivate: [authGuard]
    },
    { 
        path: 'roster-route/:rosterId', 
        loadChildren: () => import('./modules/roster-route/roster-route.module').then(m => m.RosterRouteModule),
        canActivate: [authGuard]
    },
    { 
        path: 'view-route/:rosterId', 
        loadChildren: () => import('./modules/roster-route/roster-route.module').then(m => m.RosterRouteModule),
    },
    {
        path: 'crm/contractor',
        loadChildren: () => import('./modules/contractor-crm/contractor-crm.module').then(m => m.ContractorCrmModule),
        canActivate: [authGuard]
    },
    {
        path: 'crm/tickets',
        loadComponent: () => import('./modules/tickets/tickets.component').then(c => c.TicketsComponent),
        canActivate: [authGuard]
    },
    {
        // CRM > Tasks - everything assigned to the signed-in admin.
        path: 'crm/tasks',
        loadComponent: () => import('./modules/tasks/tasks.component').then(c => c.TasksComponent),
        canActivate: [authGuard]
    },
    { path: 'crm/my-tasks', redirectTo: 'crm/tasks' },
    // CRM modules mirroring Zoho CRM (Invoices, Quotes, Products, Signed
    // documents, Campaigns, ...) - one list + detail page for all of them.
    ...MIRROR_MODULES.flatMap(m => [
        {
            path: `crm/${m.key}`,
            loadComponent: () => import('./modules/crm-mirror/list/mirror-list.component').then(c => c.MirrorListComponent),
            canActivate: [authGuard],
            data: { resource: m.key }
        },
        {
            path: `crm/${m.key}/:uuid`,
            loadComponent: () => import('./modules/crm-mirror/detail/mirror-detail.component').then(c => c.MirrorDetailComponent),
            canActivate: [authGuard],
            data: { resource: m.key }
        }
    ]),
    {
        path: 'crm/reporting',
        loadComponent: () => import('./modules/reporting/reporting.component').then(c => c.ReportingComponent),
        canActivate: [authGuard]
    },
    {
        path: 'crm/customer',
        loadChildren: () => import('./modules/customer-crm/customer-crm.module').then(m => m.CustomerCrmModule),
        canActivate: [authGuard]
    },
    {
        path: 'crm/carriers',
        loadChildren: () => import('./modules/carrier-crm/carrier-crm.module').then(m => m.CarrierCrmModule),
        canActivate: [authGuard]
    },
    {
        path: 'crm/supplier',
        loadChildren: () => import('./modules/supplier-crm/supplier-crm.module').then(m => m.SupplierCrmModule),
        canActivate: [authGuard]
    },
    {
        path: 'drivers',
        loadChildren: () => import('./modules/driver-hub/driver-hub.module').then(m => m.DriverHubModule),
        canActivate: [authGuard]
    },
    {
        path: 'driver-payments',
        loadChildren: () => import('./modules/driver-payments/driver-payments.module').then(m => m.DriverPaymentsModule),
        canActivate: [authGuard]
    },
    {
        path: 'clients',
        loadChildren: () => import('./modules/client-hub/client-hub.module').then(m => m.ClientHubModule),
        canActivate: [authGuard]
    },
    {
        // Contacts > Carriers - approved carriers promoted out of Carrier CRM
        // (crm/carriers above), same relationship clients/drivers have to
        // crm/customer and crm/contractor.
        path: 'carriers',
        loadComponent: () => import('./modules/carrier-hub/carrier-hub.component').then(c => c.CarrierHubComponent),
        canActivate: [authGuard]
    },
    {
        path: 'carriers/:uuid',
        loadComponent: () => import('./modules/carrier-hub/edit/edit.component').then(c => c.EditComponent),
        canActivate: [authGuard]
    },
    {
        // Contacts > Suppliers - approved suppliers promoted out of Supplier
        // CRM (crm/supplier above), same relationship carriers/clients/drivers
        // have to crm/carriers/crm/customer/crm/contractor.
        path: 'suppliers',
        loadComponent: () => import('./modules/supplier-hub/supplier-hub.component').then(c => c.SupplierHubComponent),
        canActivate: [authGuard]
    },
    {
        path: 'suppliers/:uuid',
        loadComponent: () => import('./modules/supplier-hub/edit/edit.component').then(c => c.EditComponent),
        canActivate: [authGuard]
    },
    {
        // Supplier profile & supplied drivers - the Supplier Portal's own Profile
        // and Drivers data for one supplier (?tab=drivers opens Supplied Drivers).
        path: 'suppliers/:uuid/details',
        loadComponent: () => import('./modules/supplier-hub/details/details.component').then(c => c.DetailsComponent),
        canActivate: [authGuard]
    },
    {
        path: 'client-invoice',
        loadChildren: () => import('./modules/client-invoice/client-invoice.module').then(m => m.ClientInvoiceModule),
        canActivate: [authGuard]
    },
    {
        path: 'driver-compliance',
        loadChildren: () => import('./modules/driver-compliance/driver-compliance.module').then(m => m.DriverComplianceModule),
        canActivate: [authGuard]
    },
    {
        path: 'consignments',
        loadChildren: () => import('./modules/consignments-v3/consignments-v3.module').then(m => m.ConsignmentsV3Module),
        canActivate: [authGuard]
    },
    {
        // Same module/component as Consignments, scoped to service_type.shift_type_id = 2 ("Job") via route data.
        path: 'jobs',
        loadChildren: () => import('./modules/consignments-v3/consignments-v3.module').then(m => m.ConsignmentsV3Module),
        canActivate: [authGuard],
        data: { shiftTypeId: 2 }
    },
    {
        path: 'posts',
        loadChildren: () => import('./modules/posts/posts.module').then(m => m.PostsModule),
        canActivate: [authGuard]
    },
    {
        path: 'incident-reports',
        loadChildren: () => import('./modules/incident-reports/incident-reports.module').then(m => m.IncidentReportsModule),
        canActivate: [authGuard]
    },
    {
        path: 'settings',
        loadChildren: () => import('./modules/settings/settings.module').then(m => m.SettingsModule),
        canActivate: [authGuard]
    },
    {
        path: 'workflow',
        loadChildren: () => import('./modules/workflow/workflow.module').then(m => m.WorkflowModule),
        canActivate: [authGuard]
    },
    {
        path: 'driver-location',
        loadChildren: () => import('./modules/driver-location/driver-location.module').then(m => m.DriverLocationModule),
        canActivate: [authGuard]
    },
    {
        path: 'users',
        loadChildren: () => import('./modules/user-management/user-management.module').then(m => m.UserManagementModule),
        canActivate: [authGuard]
    },
    {
        // Same module/component as crm/customer's Tree view, scoped to
        // tree-only via route data (same pattern as jobs/shiftTypeId above) -
        // Companies CRM's Company Tree, surfaced here as its own Admin page.
        path: 'customer-hierarchy',
        loadChildren: () => import('./modules/customer-crm/customer-crm.module').then(m => m.CustomerCrmModule),
        canActivate: [authGuard],
        data: { treeOnly: true }
    },
    {
        // Admin > Customer Carrier Accounts - which active carriers are
        // assigned to which active customers (plain many-to-many, table view only).
        path: 'customer-carrier-accounts',
        loadComponent: () => import('./modules/customer-carrier-accounts/customer-carrier-accounts.component').then(c => c.CustomerCarrierAccountsComponent),
        canActivate: [authGuard]
    },
    {
        // Admin > Integrations - third-party connections (Zoho CRM today).
        path: 'integrations',
        loadComponent: () => import('./modules/integrations/integrations.component').then(c => c.IntegrationsComponent),
        canActivate: [authGuard]
    },
    {
        // Admin > Training Modules - course library, assignments and My training.
        path: 'training',
        loadComponent: () => import('./modules/training/training.component').then(c => c.TrainingComponent),
        canActivate: [authGuard]
    },
    {
        path: 'training/courses/:uuid',
        loadComponent: () => import('./modules/training/course-builder/course-builder.component').then(c => c.CourseBuilderComponent),
        canActivate: [authGuard]
    },
    {
        // Share links: /training/c/{slug} and /training/c/{slug}/{moduleId}. No authGuard -
        // a public course opens for guests; a private one sends them through the Microsoft login.
        path: 'training/c/:slug',
        loadComponent: () => import('./modules/training/course-player/course-player.component').then(c => c.CoursePlayerComponent)
    },
    {
        path: 'training/c/:slug/:moduleId',
        loadComponent: () => import('./modules/training/course-player/course-player.component').then(c => c.CoursePlayerComponent)
    },
    {
        path: 'training/learn/:uuid',
        loadComponent: () => import('./modules/training/course-player/course-player.component').then(c => c.CoursePlayerComponent),
        canActivate: [authGuard]
    },
    {
        path: 'activity-log',
        loadChildren: () => import('./modules/activity-log/activity-log.module').then(m => m.ActivityLogModule),
        canActivate: [authGuard]
    },
    {
        path: 'fleet-management',
        loadChildren: () => import('./modules/fleet-management/fleet-management.module').then(m => m.FleetManagementModule),
        canActivate: [authGuard]
    },
    {
        path: 'roster-management',
        loadChildren: () => import('./modules/roster-management/roster-management.module').then(m => m.RosterManagementModule),
        canActivate: [authGuard]
    },
    {
        path: 'route-planner',
        loadChildren: () => import('./modules/route-planner/route-planner.module').then(m => m.RoutePlannerModule),
        canActivate: [authGuard]
    },
    {
        path: 'auth',
        loadChildren: () => import('./modules/auth/auth.module').then(m => m.AuthModule)
    },
    {
        path: 'reset-password',
        loadChildren: () => import('./modules/password-reset/password-reset.module').then(m => m.PasswordResetModule)
    },
    {
        path: 'shifts',
        loadChildren: () => import('./modules/shifts-v3/shifts-v3.module').then(m => m.ShiftsV3Module),
        canActivate: [authGuard]
    },
    {
        path: 'expired-documents',
        loadChildren: () => import('./modules/expired-documents/expired-documents.module').then(m => m.ExpiredDocumentsModule),
        canActivate: [authGuard]
    },
    {
        path: 'driver-availability',
        loadChildren: () => import('./modules/driver-availability/driver-availability.module').then(m => m.DriverAvailabilityModule),
        canActivate: [authGuard]
    },
    {
        path: 'messages',
        loadChildren: () => import('./modules/messaging/messaging.module').then(m => m.MessagingModule),
        canActivate: [authGuard]
    },
    {
        path: 'ecommerce',
        loadChildren: () => import('./modules/ecommerce/ecommerce.module').then(m => m.EcommerceModule),
        canActivate: [EcommerceGuard]

    },
    {
        path: 'ecommerce-login',
        loadChildren: () => import('./modules/ecommerce/login/login.module').then(m => m.LoginModule)
    },
    {
        path: 'invalid-page',
        loadChildren: () => import('./modules/invalid-page/invalid-page.module').then(m => m.InvalidPageModule),
    },
    {
        path: 'login',
        redirectTo: '/custm-login',
        pathMatch: "full"
    },
    {
        path: 'shift-tracking/:token',
        loadComponent: () => import('./modules/ecommerce/public/shift-tracking/shift-tracking.component').then(c => c.ShiftTrackingComponent)
    },
    {
        path: 'shipment-tracking/:token',
        loadComponent: () => import('./modules/ecommerce/public/shipment-tracking/shipment-tracking.component').then(c => c.ShipmentTrackingComponent)
    },
    {
        path: 'supplier-login',
        loadComponent: () => import('./modules/supplier-portal/components/login/login.component').then(c => c.LoginComponent),
        title: 'Custm | Supplier Login'
    },
    {
        path: 'supplier-portal',
        loadChildren: () => import('./modules/supplier-portal/supplier-portal.module').then(m => m.SupplierPortalModule),
        title: 'Custm | Supplier Portal'
    },
    {
        path: "",
        redirectTo: "/ecommerce-login", 
        pathMatch: "full"
    },
    {
        path: "**",
        redirectTo: "/invalid-page"
    }
];

@NgModule({
    imports: [RouterModule.forRoot(routes)],
    exports: [RouterModule]
})
export class AppRoutingModule {}